/**
 * Why a turn did not come back, in the only terms the UI needs.
 *
 * The chat cannot use the app's shared error mapper: it turns every 429 into one message, and a
 * timeout into "check your connection". The chat has to tell apart three 429s (a minute's limit,
 * the day's spend cap, and the API's general limiter) and — the one that matters most — whether
 * the request got far enough that the week may ALREADY have changed. Saying "nothing happened"
 * when a dish was in fact swapped is the one answer that would make someone eat the wrong thing.
 *
 * `sent: false` means the turn provably never reached the model, so nothing can have changed and
 * the message can go straight back into the composer. `sent: true` means we do not know.
 */
export type AssistantFailureKind =
  /** The browser said it was offline before anything left: nothing was sent. */
  | "offline_before"
  /**
   * The connection failed once the request had been handed to the network. `fetch` only resolves
   * when the whole turn is over (the server answers once, at the end), so a drop while waiting
   * looks exactly like a connection that never opened. We cannot tell them apart, so we assume the
   * worse one: the week may have changed.
   */
  | "offline_after"
  /** 400: we built a request the server will not take. */
  | "bad_request"
  /** 401: the session expired. */
  | "session"
  /** 404: the server's own switch is off. The chat hides itself. */
  | "disabled"
  /** 429, this minute's limit: ten messages a minute, per person. */
  | "rate_minute"
  /** 429, the day's spend cap. */
  | "rate_daily"
  /** 500: it broke. The week may have changed. */
  | "server"
  /** 503: the model is unavailable right now. The week may have changed. */
  | "unavailable"
  /** Our own 30 s wait ran out. The turn may still be finishing; the week may have changed. */
  | "timeout"
  /** 502, 504, a cancelled request or a reply we could not read. The week may have changed. */
  | "unknown";

/** Failures where the request provably never reached the model, so the week cannot have changed. */
const NEVER_SENT: ReadonlySet<AssistantFailureKind> = new Set<AssistantFailureKind>([
  "offline_before",
  "bad_request",
  "session",
  "disabled",
  "rate_minute",
  "rate_daily",
]);

export class AssistantError extends Error {
  readonly kind: AssistantFailureKind;
  /** Whether the turn may have reached the model, and so may have changed the week. */
  readonly sent: boolean;
  /** Seconds to wait, when the server said so (`retryAfter` or `Retry-After`). */
  readonly retryAfterSeconds?: number;

  constructor(kind: AssistantFailureKind, options: { retryAfterSeconds?: number } = {}) {
    super(`assistant turn failed: ${kind}`);
    this.name = "AssistantError";
    this.kind = kind;
    this.sent = !NEVER_SENT.has(kind);
    this.retryAfterSeconds = options.retryAfterSeconds;
  }
}

/** True when the week must be read again before anything is said about it. */
export function needsWeekReread(error: unknown): boolean {
  return error instanceof AssistantError && error.sent;
}
